<template lang="pug">
.data-table(:class="dataTableClasses")
  .data-table__table
    UiDataTable(
      :headers="headers"
      :items="items"
      :sortBy="sortBy"
      :sortOrder="sortOrder"
      :loading="progress"
      :stickyHeader="stickyHeader"
      @sort="onSort"
      size="md"
      ref="uiDataTable"
    )
      template(
        v-for="header in headers"
        v-slot:[`cell:${header.key}`]="props"
      )
        slot(
          :name="`cell:${header.key}`"
          v-bind="props"
        )

  DataTableEmptyState(v-if="error && !progress")
    template(#title) {{ $t("rest.error") }}
    template(#description) {{ errorDetailsMessage }}

  DataTableEmptyState.data-table__empty-state(
    v-else-if="progress && !items.length"
  )
    template(#title)
      slot(name="loading_title") {{ $t("rest.no_data") }}
    template(#description)
      slot(name="loading_description") {{ $t("rest.fetching_data") }}
  DataTableEmptyState.data-table__empty-state(
    v-else-if="!progress && !items.length"
  )
    template(#title)
      slot(name="empty_title") {{ $t("rest.no_data") }}
    template(#description)
      slot(name="empty_description") {{ $t("rest.empty") }}
  .sticky-footer
    .fake-scrollbar-container
      .fake-scrollbar-content
    RestTablePagination(
      v-if="!disablePagination"
      v-model:pageModel="pageModel"
      v-model:perPageModel="perPageModel"
      :perPageOptions="perPageOptions"
      :itemsPerPageAttachDropdownToBody="itemsPerPageAttachDropdownToBody"
      :totalItems="totalItems"
      :filters="filters"
    )
</template>

<script>
import RestTablePagination from "../RestTable/RestTablePagination.vue";
import { defineComponent } from "vue";

export default defineComponent({
  components: {
    RestTablePagination,
  },
  props: {
    filters: {
      type: Object,
      default: () => ({}),
    },
    isLoading: {
      type: Boolean,
    },
    errorDetailsMessage: {
      type: String,
      default: null,
    },
    error: {
      type: Object,
      default: null,
    },
    progress: {
      type: Boolean,
      default: false,
    },
    headers: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    embed: {
      type: Boolean,
      default: false,
    },
    sortBy: {
      type: String,
      default: null,
    },
    sortOrder: {
      type: String,
      default: "asc",
    },
    page: {
      type: Number,
      default: null,
    },
    perPage: {
      type: Number,
      default: null,
    },
    totalItems: {
      type: Number,
      default: null,
    },
    disablePagination: {
      type: Boolean,
      default: false,
    },
    perPageOptions: {
      type: Array,
      default: undefined,
    },
    stickyHeader: {
      type: Boolean,
      default: true,
    },
    itemsPerPageAttachDropdownToBody: {
      type: Boolean,
    },
    updateFilters: {
      type: Function,
      required: true,
    },
  },

  data() {
    return {
      tableWrapper: null,
      mainTable: null,
      fakeScrollbar: null,
      isSyncing: false,
      resizeObserver: null,
    };
  },

  computed: {
    pageModel: {
      get() {
        return this.page ?? 1;
      },
      set(value) {
        this.updateFilters({ page: value });
      },
    },
    perPageModel: {
      get() {
        return this.perPage ?? null;
      },
      set(value) {
        this.updateFilters({ per_page: value, page: 1 });
      },
    },
    dataTableClasses() {
      return {
        "data-table--embed": this.embed,
      };
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.initScrollSync();
      this.initResizeObserver();
    });
  },
  beforeUnmount() {
    if (this.fakeScrollbar) {
      this.fakeScrollbar.removeEventListener("scroll", this.handleFakeScroll);
    }
    if (this.tableWrapper) {
      this.tableWrapper.removeEventListener("scroll", this.handleRealScroll);
    }
    if (this.resizeObserver && this.mainTable) {
      this.resizeObserver.unobserve(this.mainTable);
    }
  },
  methods: {
    initScrollSync() {
      const uiDataTableHTMLElement = this.$refs.uiDataTable?.$el;
      if (!uiDataTableHTMLElement) return;

      this.tableWrapper = uiDataTableHTMLElement.querySelector(
        ".ui-data-table__body-wrapper",
      );
      this.mainTable = this.tableWrapper?.querySelector("table");
      this.fakeScrollbar = this.$el.querySelector(".fake-scrollbar-container");

      if (!this.tableWrapper || !this.mainTable || !this.fakeScrollbar) return;

      this.updateFakeScrollbarWidth();

      this.fakeScrollbar.addEventListener("scroll", this.handleFakeScroll, {
        passive: true,
      });
      this.tableWrapper.addEventListener("scroll", this.handleRealScroll, {
        passive: true,
      });
    },
    initResizeObserver() {
      if (!this.mainTable) return;
      this.resizeObserver = new ResizeObserver(() => {
        this.updateFakeScrollbarWidth();
      });

      this.resizeObserver.observe(this.mainTable);
    },
    updateFakeScrollbarWidth() {
      if (!this.mainTable || !this.fakeScrollbar) return;
      const fakeScrollbarContent = this.fakeScrollbar.querySelector(
        ".fake-scrollbar-content",
      );
      if (fakeScrollbarContent) {
        fakeScrollbarContent.style.width = `${this.mainTable.scrollWidth}px`;
      }
    },

    handleFakeScroll() {
      if (this.isSyncing) return;
      this.isSyncing = true;
      this.tableWrapper.scrollLeft = this.fakeScrollbar.scrollLeft;
      this.isSyncing = false;
    },

    handleRealScroll() {
      if (this.isSyncing) return;
      this.isSyncing = true;
      this.fakeScrollbar.scrollLeft = this.tableWrapper.scrollLeft;
      this.isSyncing = false;
    },
    headerClasses(header) {
      return {
        squeeze: !!header.squeeze,
        sortable: !!header.sortable,
        nowrap: !(header.wrap ?? true),
        "is-sorted": this.sortBy === header.key,
        [`sort-order--${this.sortOrder}`]: this.sortBy === header.key,
      };
    },

    celClasses(header) {
      const list = {};

      list["squeeze"] = !!header.squeeze;
      list["is-sorted"] = this.sortBy === header.key;

      return list;
    },

    onSort(event) {
      this.$emit("sort", event);
    },

    onHeaderClick(e, header) {
      this.$emit("headerClick", header);

      if (header.sortable) {
        const by = this.sortBy;
        const order = this.sortOrder;
        let sortBy = by;
        let sortOrder = order;
        if (header.key === by) {
          if (order === "desc") {
            sortOrder = "asc";
          } else {
            sortBy = null;
            sortOrder = "desc";
          }
        } else {
          sortBy = header.key;
          sortOrder = "desc";
        }
        this.$emit("sort", { sortBy, sortOrder });
      }
    },
  },
});
</script>

<style lang="scss">
@import "@/assets/stylesheet/_variables.scss";

$embed-padding: 1.5rem;
$fake-scroll-height: 17px;

.data-table {
  --ui-data-table-sticky-header--top: var(--header-height);

  .ui-data-table__body-wrapper {
    -ms-overflow-style: none; // hide scrollbar for IE and Edge
    scrollbar-width: none; // hide scrollbar for Firefox

    &::-webkit-scrollbar {
      display: none; // hide scrollbar for Chrome, Safari and Opera
    }
  }

  .sticky-footer {
    position: sticky;
    border-top: 1px solid var(--bb-gray-900); // current table border
    bottom: 0;
    margin-top: -1px; // preventing a double-border effect when the element is not sticky
  }

  .fake-scrollbar-container {
    overflow-x: auto;
    height: $fake-scroll-height;
    margin-top: -$fake-scroll-height;
  }

  .fake-scrollbar-content {
    height: 1px;
  }

  .ui-data-table__body-wrapper,
  .ui-data-table__sticky-header-wrapper {
    .ui-table {
      th,
      td {
        word-break: normal;
      }
    }
  }

  &--embed {
    padding: 0 1rem;
  }

  .ui-card-section &--embed {
    margin-left: -1rem;
    margin-right: -1rem;
  }

  &__table {
    overflow-x: auto;
  }

  &__table .ui-table__header-cell:first-child,
  &__table .ui-table__cell:first-child {
    padding-left: 1.5rem;
  }

  &__table .ui-table__header-cell:last-child,
  &__table .ui-table__cell:last-child {
    padding-right: 1.5rem;
  }

  & .table {
    margin: 0;
    border-bottom: 1px solid $bb-gray-900;
  }

  & .table th {
    background: $white;
    font-weight: 500;
    transition: all $bb-duration;
    border-top: 1px solid $bb-gray-900;
    border-bottom: 1px solid $bb-gray-800;
    vertical-align: middle;

    &.squeeze {
      width: 0.1%;
    }

    &.sortable {
      cursor: pointer;

      &:hover {
        background: mix($primary, $white, 10%);
        color: $primary;
        border-bottom-color: $primary;
      }
    }

    &.nowrap {
      white-space: nowrap;
    }
  }

  & .table td {
    transition: all $bb-duration;
    border-top: 1px solid $bb-gray-900;
    vertical-align: middle;
  }

  & .table tr:hover td {
    background: mix($white, $bb-gray-900, 50%);
  }

  & .table th.is-sorted,
  & .table td.is-sorted {
    background: mix($white, $bb-gray-900, 50%);
  }

  & .table tr:hover td.is-sorted {
    background: $bb-gray-900;
  }

  & .table #{&}__progress-row {
    & th {
      border-color: transparent;
      padding: 0 !important;
      position: relative;
    }

    & .progress {
      border-radius: 0;
      position: absolute;
      width: 100%;
      left: 0;
      height: 0px;
      transition: all $bb-duration;
    }

    &.active .progress {
      height: 4px;
    }
  }

  & .table-info-row td {
    background: mix($white, $bb-gray-900, 50%) !important;
  }
}

.data-table__header-cel {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
}

.data-table__header-title {
  flex-grow: 1;
}

.data-table__sort {
  display: none;
  position: relative;
  flex-shrink: 0;
  margin-left: 10px;
  width: 10px;
  height: 20px;

  span {
    pointer-events: none;
    position: absolute;
    top: 50%;
    left: 50%;
    font-size: 20px;
    color: $bb-gray-800;
    transform: translate(-50%, -50%);
    transition: all $bb-duration;

    &:first-child {
      margin-top: -5px;
    }

    &:last-child {
      margin-top: 5px;
    }
  }

  .table th:hover & span {
    color: mix($white, $primary, 70%);
  }

  .table th.sort-order--asc & span:first-child,
  .table th.sort-order--desc & span:last-child {
    color: $primary;
  }
}

.table th.sortable .data-table__sort {
  display: block;
}

.data-table--embed {
  & .table td:first-child,
  & .table th:first-child {
    padding-left: $embed-padding;
  }

  & .table td:last-child,
  & .table th:last-child {
    padding-right: $embed-padding;
  }
}
</style>
